import React, { useState } from 'react';
import { Form, Input, Button, Space, Modal, message, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { AdvClient, AdvClientRequest } from '../../types/adv';
import { advClientService } from '../../services/advClientService';

interface AdvClientFormProps {
  client?: AdvClient;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdvClientForm: React.FC<AdvClientFormProps> = ({
  client,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const isEditing = !!client;
  const title = isEditing 
    ? t('advertising.clients.form.edit_title') 
    : t('advertising.clients.form.create_title');

  const handleSubmit = async (values: AdvClientRequest) => {
    setIsLoading(true);
    try {
      if (isEditing && client) {
        await advClientService.updateAdvClient(client.id, values);
        message.success(t('advertising.clients.notifications.update_success'));
      } else {
        await advClientService.createAdvClient(values);
        message.success(t('advertising.clients.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting advertising client form:', error);
      message.error(t('advertising.clients.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when modal opens or client changes
  React.useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        client_name: client?.client_name || '',
        client_active: client?.client_active ?? true,
      });
    }
  }, [form, client, isVisible]);

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          client_name: client?.client_name || '',
          client_active: client?.client_active ?? true,
        }}
      >
        <Form.Item
          name="client_name"
          label={t('advertising.clients.form.name')}
          rules={[
            {
              required: true,
              message: t('advertising.clients.form.name_required'),
            },
          ]}
        >
          <Input placeholder={t('advertising.clients.form.name_placeholder')} />
        </Form.Item>

        <Form.Item
          name="client_active"
          label={t('advertising.clients.form.active')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t('advertising.clients.form.submit')}
            </Button>
            <Button onClick={onClose}>
              {t('advertising.clients.form.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdvClientForm; 